import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  debounce,
} from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getApiVariablesDevices } from '@/api/gen/api';
import { useDebouncedEvent } from 'react-admin';

// minimal variable-like shape accepted by this selector
export interface DeviceOption {
  id: number;
  variableName: string;
  // allow extra fields from API types
  [key: string]: any;
}

interface DeviceSelectorProps {
  plantId?: number;
  value?: number | null; // variableId
  onChange: (variableId: number | null, variable?: DeviceOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
}

export default function DeviceSelector({
  plantId,
  value,
  onChange,
  placeholder = 'Selecciona una variable DEVICE',
  disabled = false,
  required = false,
  label = 'Variable DEVICE',
}: DeviceSelectorProps) {
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = debounce;

  // Store previous plantId to detect changes
  const prevPlantIdRef = useRef<number | undefined>(undefined);

  // Reset input value and selected value when plantId changes to a different value
  useEffect(() => {
    if (prevPlantIdRef.current !== plantId) {
      setInputValue('');
      setSearchQuery('');
      onChange(null, null);
    }
    prevPlantIdRef.current = plantId;
  }, [plantId, onChange]);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['deviceVariables', 'infinite', plantId, debouncedSearch],
    queryFn: ({ pageParam }) => {
      const params: any = {
        SearchString: debouncedSearch || undefined,
        page: pageParam as number,
        pageSize: 20,
      };
      if (plantId) params.PlantId = plantId;
      return getApiVariablesDevices(params).then((response: any) => response);
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.items.length, 0);
      if (loaded < (lastPage.totalCount || 0)) {
        return (lastPage.page || 1) + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!plantId, // Fetch when plantId is provided
  });

  const options = useMemo(() => {
    const allOpts = (data?.pages?.flatMap(page => page?.items || []) ||
      []) as DeviceOption[];
    // Ensure uniqueness by id
    const uniqueOpts = [...new Map(allOpts.map(opt => [opt.id, opt])).values()];
    // Always include the selected variable if it exists and is not already in the list
    const selectedVariable = allOpts.find(variable => variable.id === value);
    if (
      value &&
      selectedVariable &&
      !uniqueOpts.some(opt => opt.id === value)
    ) {
      uniqueOpts.unshift(selectedVariable);
    }
    return uniqueOpts;
  }, [data, value]);

  // Find the selected variable based on variableId
  const selectedVariable = useMemo(() => {
    return options.find(variable => variable.id === value) || null;
  }, [options, value]);

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >=
        listboxNode.scrollHeight - 50 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  const renderOptionFn = (props: any, option: any) => {
    const { key, ...other } = props;
    return (
      <li key={key} {...other}>
        <div>
          <div
            style={{
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {option.variableName}
          </div>
          <div style={{ color: 'gray', fontSize: '0.875rem' }}>
            {option.deviceType}
          </div>
        </div>
      </li>
    );
  };

  return (
    <Autocomplete
      disablePortal
      freeSolo
      value={selectedVariable}
      inputValue={inputValue}
      onChange={(_, newValue) => {
        if (typeof newValue === 'object' && newValue !== null) {
          onChange(newValue.id, newValue);
        } else {
          // If string or null, do nothing or deselect if needed
          onChange(null, null);
        }
      }}
      onInputChange={(_, newInputValue, reason) => {
        setInputValue(newInputValue);
        if (reason === 'input') {
          setSearchQuery(newInputValue);
          if (value) {
            onChange(null, null);
          }
        }
      }}
      options={options}
      getOptionLabel={option =>
        typeof option === 'string' ? option : option.variableName
      }
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      loading={isLoading}
      disabled={disabled || !plantId}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={!!error}
          helperText={
            error
              ? 'Error cargando variables DEVICE'
              : data?.pages?.[data.pages.length - 1]?.totalCount
                ? `${data.pages[data.pages.length - 1].totalCount} variables`
                : undefined
          }
          sx={{
            '& .MuiInputBase-input': {
              fontSize: value ? '0.75rem' : '1rem',
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color='inherit' size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={renderOptionFn}
      ListboxProps={{
        onScroll: handleScroll,
      }}
      noOptionsText={
        !plantId
          ? 'Selecciona una planta primero'
          : isLoading
            ? 'Cargando...'
            : 'No hay opciones'
      }
    />
  );
}
